import React from 'react';
import { Eye, Ban, UserCheck, Search, Filter, Calendar, Music, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from './adminShared';

// Admin dashboard "users" tab (extracted from AdminPage).
export const AdminUsersTab = ({
  dateFilter,
  filteredUsers,
  handleBanUser,
  handleSetUserRole,
  handleUnbanUser,
  navigate,
  resetFilters,
  roleFilter,
  searchQuery,
  setDateFilter,
  setRoleFilter,
  setSearchQuery,
  setStatusFilter,
  statusFilter,
  user,
  users,
}) => (
  <div className="space-y-4">
    {/* Filters Bar */}
    <div className="flex flex-wrap items-center gap-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
      <div className="flex items-center gap-2 text-zinc-400">
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">Filters:</span>
      </div>

      {/* Search */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />
      </div>

      {/* Date Filter */}
      <Select value={dateFilter} onValueChange={setDateFilter}>
        <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <Calendar className="w-4 h-4 mr-2" />
          <SelectValue placeholder="Date" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="all" className="text-white">All Time</SelectItem>
          <SelectItem value="today" className="text-white">Today</SelectItem>
          <SelectItem value="week" className="text-white">This Week</SelectItem>
          <SelectItem value="month" className="text-white">This Month</SelectItem>
        </SelectContent>
      </Select>

      {/* Status Filter */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="all" className="text-white">All Status</SelectItem>
          <SelectItem value="active" className="text-white">Active</SelectItem>
          <SelectItem value="banned" className="text-white">Banned</SelectItem>
        </SelectContent>
      </Select>

      {/* Role Filter — useful for "Copy all Listener emails" / "Copy all Artist emails" */}
      <Select value={roleFilter} onValueChange={setRoleFilter}>
        <SelectTrigger
          className="w-[120px] bg-zinc-800 border-zinc-700 text-white text-sm"
          data-testid="admin-users-role-filter"
        >
          <SelectValue placeholder="Role" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="all" className="text-white">All Roles</SelectItem>
          <SelectItem value="listener" className="text-white">Listeners</SelectItem>
          <SelectItem value="artist" className="text-white">Artists</SelectItem>
        </SelectContent>
      </Select>

      <Button 
        variant="outline" 
        size="sm" 
        onClick={resetFilters}
        className="bg-transparent border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white"
      >
        Reset
      </Button>

      <span className="text-xs text-zinc-500 ml-auto">{filteredUsers.length} users</span>
    </div>

    {/* Users List */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">User</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Email</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Role</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Videos</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredUsers.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-800/30">
                <td className="px-5 py-4">
                  <button
                    onClick={() => navigate(`/creator/${u.id}`)}
                    className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity group"
                    title={`View ${u.username || 'user'}'s profile`}
                    data-testid={`admin-user-profile-link-${u.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {u.avatar ? (
                        <img src={`${API}/api/files/${u.avatar}`} alt={u.username || 'avatar'} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-medium">{(u.username || u.email || '?').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <span className="font-medium text-white group-hover:text-orange-400 group-hover:underline transition-colors">
                      {u.username || 'No username'}
                    </span>
                  </button>
                </td>
                <td className="px-5 py-4 text-zinc-400">{u.email}</td>
                <td className="px-5 py-4">
                  <button
                    onClick={() => handleSetUserRole(u.id, u.role === 'artist' ? 'listener' : 'artist')}
                    className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
                      u.role === 'artist' 
                        ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30' 
                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    }`}
                    title={`Click to make ${u.role === 'artist' ? 'Listener' : 'Artist'}`}
                  >
                    {u.role === 'artist' ? (
                      <><Music className="w-3 h-3" /> Artist</>
                    ) : (
                      <><Headphones className="w-3 h-3" /> Listener</>
                    )}
                  </button>
                </td>
                <td className="px-5 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    u.is_banned ? 'bg-red-500/20 text-red-400' : 
                    u.is_premium ? 'bg-purple-500/20 text-purple-400' : 
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {u.is_banned ? 'Banned' : u.is_premium ? 'Premium' : 'Active'}
                  </span>
                </td>
                <td className="px-5 py-4 text-zinc-400">{u.video_count || 0}</td>
                <td className="px-5 py-4">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/creator/${u.id}`)}
                      className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {u.is_banned ? (
                      <button 
                        onClick={() => handleUnbanUser(u.id)}
                        className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleBanUser(u.id)}
                        className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);
